import { useGetAlboRecord } from '@framework/common-web/register-records/frontoffice/api/albo-record-controller/albo-record-controller.ts';
import { type GetAlboRecordParams } from '@framework/common-web/register-records/frontoffice/api/schemas/getAlboRecordParams.ts';
import { useRegisterRecordSearchableFields } from '@framework/common-web/register-records/hooks/useRegisterRecordSearchableFields.tsx';
import { useRegisterRecordsHandleTableChange } from '@framework/common-web/register-records/hooks/useRegisterRecordsHandleTableChange.ts';
import { useRegisterRecordsTable } from '@framework/common-web/register-records/hooks/useRegisterRecordsTable.ts';
import { RegisterRecordFieldVisibility } from '@framework/common-web/register-records/schema/RegisterRecordFieldVisibility.ts';
import { createRecordTypesListParamsValidator } from '@framework/common-web/register-records/validation/createRecordTypesListParamsValidator.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { FilterBar } from '@framework/ui/components/search/filter-bar/FilterBar.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Divider } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { useTheme } from 'styled-components';
import { returnFocusToPageTitle } from '../../../ui/layout/header/returnFocusToPageTitle.tsx';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { useRegisterRecordsTypeFo } from '../hooks/useRegisterRecordsTypeFo.ts';
import { FrontofficeRegisterRecordExportDropDown } from './FrontofficeRegisterRecordExportDropDown.tsx';

const PAGE_TITLE_ID = 'register-list-title-focus-id';

interface Params {
  id: string;
}

export type IRegisterRecordListPageProps = RouteComponentProps<Params>;

export default function RegisterRecordListPage(
  props: IRegisterRecordListPageProps,
) {
  const [registerRecordTypeId] = useIdFromUrl(props, 'id');
  const { t } = useTranslation();
  const theme = useTheme();
  const registerRecordType = useRegisterRecordsTypeFo(
    registerRecordTypeId ?? '<unknown>',
  );

  const history = useHistory();

  const defaultQuery: GetAlboRecordParams = useMemo(
    () => ({
      idTipoAlbo: registerRecordTypeId!,
      ordinamenti: [],
      pagina: 0,
      limite: 10,
    }),
    [registerRecordTypeId],
  );

  const [query, setQuery] = usePaginationUrlQueryState<GetAlboRecordParams>({
    history,
    input: { ...defaultQuery, idTipoAlbo: registerRecordTypeId! },
    persistKey: `register-records-list-fo-${registerRecordTypeId}`,
  });

  const { data, isLoading: isRegisterRecordLoading } = useGetAlboRecord(query, {
    query: { enabled: !!registerRecordTypeId, keepPreviousData: true },
  });

  const { dati: registerRecordsData, meta } = useMemo(() => {
    return data ?? {};
  }, [data]);

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: `${registerRecordType?.denominazione ?? 'Elenco'}`,
  });

  const { registerRecordColumns } = useRegisterRecordsTable({
    registerRecordType,
    registerRecordFieldVisibility: RegisterRecordFieldVisibility.FO,
  });

  const { handleTableChange } = useRegisterRecordsHandleTableChange(
    query,
    setQuery,
  );

  const searchableFields = useRegisterRecordSearchableFields(
    registerRecordType,
    RegisterRecordFieldVisibility.FO,
  );

  const schema = useMemo(
    () =>
      createRecordTypesListParamsValidator(
        RegisterRecordFieldVisibility.FO,
        registerRecordType?.configurazioneCampi ?? [],
      ),
    [registerRecordType],
  );

  const pagination = useMemo(
    () => ({
      total: meta?.totale ?? 0,
      pageSize: query.limite != null ? Number(query.limite) : 10,
      current: Number(query.pagina ?? 0) + 1,
    }),
    [query, meta?.totale],
  );

  const filterBar = (
    <FilterBar
      id={`register-record-list-search-fo-${registerRecordTypeId}`}
      key={registerRecordTypeId}
      schema={schema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      isFetching={isRegisterRecordLoading}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      total={meta?.totale ?? undefined}
      // transform={semanticTagSearchableOptions.transform}
      extraActions={
        <FrontofficeRegisterRecordExportDropDown
          registerRecordType={registerRecordType}
          query={query}
        />
      }
      layout={{ showInCard: true }}
      messageListProps={{
        message: t(
          'observation.record-albo-trovati-in-base-ai-filtri-di-ricerca',
          'record trovati in base ai filtri di ricerca.',
        ),
        resetButtonProps: {
          onClick: () => {
            setQuery(defaultQuery);
            returnFocusToPageTitle(PAGE_TITLE_ID);
          },
          children: t('observation.rimuovi-filtri', 'Rimuovi filtri'),
        },
      }}
    />
  );

  const tableTitle = useCallback(() => {
    return <div>{filterBar}</div>;
  }, [filterBar]);

  if (isRegisterRecordLoading) {
    return <LoaderSpin />;
  }
  return (
    <PageLayout isTablePage>
      <ResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        noPadding
        title={
          <TitleSubmissionRow id={PAGE_TITLE_ID}>
            {registerRecordType?.denominazione ?? 'Elenco'}
          </TitleSubmissionRow>
        }
      >
        {!theme.searchButtonOnTableFlag && <Divider style={{ margin: 0 }} />}

        <TableData
          title={tableTitle}
          darkTitleBar={false}
          showSorterTooltip={false}
          size="small"
          onChange={(pagination, _filters, sorter) => {
            handleTableChange(pagination, sorter);
          }}
          showHeader={true}
          rowKey="id"
          dataSource={registerRecordsData ?? []}
          loading={isRegisterRecordLoading}
          columns={registerRecordColumns}
          pagination={pagination}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
